import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Settings as SettingsIcon, Users, Key, DollarSign, MapPin, Shield, Link2,
  Search, ChevronRight, ChevronDown, ExternalLink, Bell, Server, Database,
  Globe, Cpu, Lock, FileText, Palette, HelpCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import SettingsAPI from './SettingsAPI';
import AdminPricing from './AdminPricing';
import AdminAPIKeysGeo from './AdminAPIKeysGeo';
import IntegrationAPIKeys from './IntegrationAPIKeys';
import BrandingSettings from './BrandingSettings';

/**
 * Page Paramètres Unifiée - Design UX amélioré
 * - Navigation par catégories avec sidebar
 * - Recherche globale
 * - Sections collapsibles
 * - Scroll fluide
 */

// Configuration des sections de paramètres
const settingsSections = [
  {
    id: 'users',
    title: 'Utilisateurs',
    description: 'Gestion des comptes et profils utilisateurs',
    icon: Users,
    color: 'blue',
    type: 'link',
    link: '/users',
    keywords: ['utilisateur', 'compte', 'profil', 'user', 'admin', 'manager']
  },
  {
    id: 'rbac',
    title: 'Rôles & Permissions',
    description: 'Contrôle d\'accès basé sur les rôles (RBAC)',
    icon: Shield,
    color: 'purple',
    type: 'link',
    link: '/rbac-management',
    keywords: ['rôle', 'permission', 'accès', 'rbac', 'autorisation', 'droit']
  },
  {
    id: 'notifications',
    title: 'Notifications & Emails',
    description: 'Configuration SMTP et notifications automatiques',
    icon: Bell,
    color: 'orange',
    type: 'link',
    link: '/settings/notifications',
    keywords: ['notification', 'email', 'smtp', 'alerte', 'rapport', 'envoi']
  },
  {
    id: 'api',
    title: 'Configuration IA',
    description: 'Clés API pour les fournisseurs LLM (OpenAI, Anthropic, Google)',
    icon: Cpu,
    color: 'emerald',
    type: 'component',
    component: SettingsAPI,
    keywords: ['ia', 'openai', 'claude', 'gpt', 'anthropic', 'gemini', 'llm', 'api']
  },
  {
    id: 'integration',
    title: 'Clés API Intégrations',
    description: 'Clés pour services externes (paiement, SMS, etc.)',
    icon: Link2,
    color: 'cyan',
    type: 'component',
    component: IntegrationAPIKeys,
    keywords: ['intégration', 'api', 'clé', 'stripe', 'twilio', 'webhook', 'externe']
  },
  {
    id: 'pricing',
    title: 'Tarification',
    description: 'Configuration des prix et marges',
    icon: DollarSign,
    color: 'green',
    type: 'component',
    component: AdminPricing,
    keywords: ['prix', 'tarif', 'marge', 'coût', 'facturation', 'pricing']
  },
  {
    id: 'geo',
    title: 'Geolocalisation',
    description: 'Configuration des services de cartographie',
    icon: MapPin,
    color: 'red',
    type: 'component',
    component: AdminAPIKeysGeo,
    keywords: ['carte', 'map', 'geo', 'mapbox', 'osm', 'openstreetmap', 'localisation']
  },
  {
    id: 'branding',
    title: 'Personnalisation Login',
    description: 'Image de fond, logo, titre de la page de connexion',
    icon: Palette,
    color: 'pink',
    type: 'component',
    component: BrandingSettings,
    keywords: ['login', 'personnalisation', 'fond', 'logo', 'branding', 'titre', 'image', 'falcon']
  }
];

// Couleurs pour les badges et icônes
const colorClasses = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
};

const iconColorClasses = {
  blue: 'text-blue-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  emerald: 'text-emerald-600',
  cyan: 'text-cyan-600',
  green: 'text-green-600',
  red: 'text-red-600',
  pink: 'text-pink-600',
};

export default function Settings() {
  const [activeSection, setActiveSection] = useState('api');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['api']);
  const navigate = useNavigate();

  // Filtrer les sections selon la recherche
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return settingsSections;
    
    const query = searchQuery.toLowerCase();
    return settingsSections.filter(section => 
      section.title.toLowerCase().includes(query) ||
      section.description.toLowerCase().includes(query) ||
      section.keywords.some(kw => kw.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  // Toggle une section collapsible
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Naviguer vers une section
  const handleSectionClick = (section) => {
    if (section.type === 'link') {
      navigate(section.link);
    } else {
      setActiveSection(section.id);
      if (!expandedSections.includes(section.id)) {
        setExpandedSections(prev => [...prev, section.id]);
      }
      // Scroll vers la section
      const element = document.getElementById(`section-${section.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Rendu d'une carte de navigation rapide
  const QuickNavCard = ({ section }) => {
    const IconComponent = section.icon;
    const isLink = section.type === 'link';
    
    return (
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${
          activeSection === section.id && !isLink ? 'ring-2 ring-primary' : ''
        }`}
        onClick={() => handleSectionClick(section)}
        data-testid={`nav-card-${section.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${colorClasses[section.color]}`}>
              <IconComponent className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm truncate">{section.title}</h3>
                {isLink && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {section.description}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Rendu d'une section de contenu
  const ContentSection = ({ section }) => {
    const IconComponent = section.icon;
    const ContentComponent = section.component;
    const isExpanded = expandedSections.includes(section.id);
    
    if (section.type === 'link') return null;
    
    return (
      <div id={`section-${section.id}`} className="scroll-mt-4">
        <Collapsible open={isExpanded} onOpenChange={() => toggleSection(section.id)}>
          <CollapsibleTrigger asChild>
            <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${colorClasses[section.color]}`}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{section.title}</CardTitle>
                      <CardDescription>{section.description}</CardDescription>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="mt-2 pl-4 border-l-2 border-muted">
              <Card>
                <CardContent className="p-6">
                  <ContentComponent />
                </CardContent>
              </Card>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                  <SettingsIcon className="h-7 w-7" />
                </div>
                Paramètres
              </h1>
              <p className="text-muted-foreground mt-1">
                Configuration générale de l&apos;application Baobab Connect
              </p>
            </div>
            
            {/* Barre de recherche */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher un paramètre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white dark:bg-slate-800"
                data-testid="settings-search"
              />
            </div>
          </div>
        </div>

        {/* Navigation rapide */}
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Navigation rapide
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {filteredSections.map(section => (
              <QuickNavCard key={section.id} section={section} />
            ))}
          </div>
          
          {filteredSections.length === 0 && (
            <Card className="p-8 text-center">
              <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                Aucun paramètre trouvé pour &quot;{searchQuery}&quot;
              </p>
              <Button 
                variant="link" 
                onClick={() => setSearchQuery('')}
                className="mt-2"
              >
                Effacer la recherche
              </Button>
            </Card>
          )}
        </div>

        {/* Sections de contenu */}
        {!searchQuery && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Configuration détaillée
            </h2>
            
            {settingsSections
              .filter(s => s.type === 'component')
              .map(section => (
                <ContentSection key={section.id} section={section} />
              ))}
          </div>
        )}

        {/* Sections filtrées avec recherche */}
        {searchQuery && filteredSections.filter(s => s.type === 'component').length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Résultats de recherche
            </h2>
            
            {filteredSections
              .filter(s => s.type === 'component')
              .map(section => (
                <ContentSection key={section.id} section={section} />
              ))}
          </div>
        )}

        {/* Footer avec liens utiles */}
        <div className="mt-12 pt-6 border-t">
          <div className="flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
            <Link to="/audit-logs" className="hover:text-primary flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Logs d&apos;audit
            </Link>
            <Link to="/system-logs" className="hover:text-primary flex items-center gap-1">
              <Server className="h-4 w-4" />
              Logs système
            </Link>
            <Link to="/sauvegardes" className="hover:text-primary flex items-center gap-1">
              <Database className="h-4 w-4" />
              Sauvegardes
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
